/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { Shield, FileText, Lock } from 'lucide-react';

interface LegalProps {
    initialTab?: 'TERMS' | 'PRIVACY';
}

export const Legal: React.FC<LegalProps> = ({ initialTab = 'TERMS' }) => {
    const [activeTab, setActiveTab] = useState<'TERMS' | 'PRIVACY'>(initialTab);

    return (
        <div className="min-h-screen bg-[#F9F8F4] flex flex-col relative animate-fade-in">
            <main className="flex-1 container mx-auto px-6 py-12 max-w-4xl">
                <div className="flex gap-4 mb-8 border-b border-stone-200">
                    <button 
                        onClick={() => setActiveTab('TERMS')}
                        className={`pb-3 px-4 font-bold text-sm transition-colors relative ${activeTab === 'TERMS' ? 'text-stone-900' : 'text-stone-400 hover:text-stone-600'}`}
                    >
                        Terms of Service
                        {activeTab === 'TERMS' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-stone-900"></div>}
                    </button>
                    <button 
                        onClick={() => setActiveTab('PRIVACY')}
                        className={`pb-3 px-4 font-bold text-sm transition-colors relative ${activeTab === 'PRIVACY' ? 'text-stone-900' : 'text-stone-400 hover:text-stone-600'}`}
                    >
                        Privacy Policy
                        {activeTab === 'PRIVACY' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-stone-900"></div>}
                    </button>
                </div>

                <div className="prose prose-stone max-w-none bg-white p-8 rounded-2xl border border-stone-200 shadow-sm">
                    {activeTab === 'TERMS' ? (
                        <div className="space-y-6">
                            <div className="flex items-center gap-3 mb-6">
                                <FileText size={32} className="text-stone-300" />
                                <h2 className="text-2xl font-serif font-bold text-stone-900 m-0">Terms of Service</h2>
                            </div>
                            <p className="text-stone-500 text-sm italic">Last Updated: October 24, 2025</p>
                            
                            <h3 className="font-bold text-lg text-stone-900">1. Acceptance of Terms</h3>
                            <p>By accessing and using the HotLaunchpad platform ("the Protocol"), you acknowledge that you have read, understood, and agree to be bound by these Terms of Service. The Protocol is a decentralized application running on the Solana and Sui blockchains.</p>

                            <h3 className="font-bold text-lg text-stone-900">2. No Financial Advice</h3>
                            <p>HotLaunchpad is a technical tool for token creation and trading. Nothing on this website constitutes financial advice. You are solely responsible for your investment decisions and token deployments.</p>

                            <h3 className="font-bold text-lg text-stone-900">3. Risks</h3>
                            <p>Cryptocurrency trading and token creation involve significant risk. You may lose all funds. Smart contracts, while audited, may still contain bugs or vulnerabilities. The Protocol is provided "AS IS" without warranty of any kind.</p>

                            <h3 className="font-bold text-lg text-stone-900">4. Prohibited Activities</h3>
                            <p>You agree not to use the Protocol for money laundering, financing terrorism, or creating tokens that violate intellectual property rights or local laws in your jurisdiction.</p>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            <div className="flex items-center gap-3 mb-6">
                                <Lock size={32} className="text-stone-300" />
                                <h2 className="text-2xl font-serif font-bold text-stone-900 m-0">Privacy Policy</h2>
                            </div>
                            <p className="text-stone-500 text-sm italic">Last Updated: October 24, 2025</p>

                            <h3 className="font-bold text-lg text-stone-900">1. Data Collection</h3>
                            <p>We prioritize your privacy. As a decentralized application, we do not collect personal identifiers like names or addresses unless you voluntarily provide them (e.g., email for embedded wallets). We collect:</p>
                            <ul className="list-disc pl-5 space-y-1 text-stone-600">
                                <li>Public wallet addresses</li>
                                <li>On-chain transaction data</li>
                                <li>Technical usage data (cookies, IP addresses for rate limiting)</li>
                            </ul>

                            <h3 className="font-bold text-lg text-stone-900">2. Embedded Wallets</h3>
                            <p>If you use email/social login features, authentication is handled by a third-party non-custodial provider. Your private keys are encrypted and stored securely; HotLaunchpad staff cannot access your funds.</p>

                            <h3 className="font-bold text-lg text-stone-900">3. Third-Party Services</h3>
                            <p>We use services like Helius, Birdeye, and Pinata for infrastructure. Your interactions with these services are subject to their respective privacy policies.</p>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};
